'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { urlForImage } from '@/sanity/lib/image';
import { X, Maximize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface InstitutionalImageProps {
  value: {
    asset: any;
    caption?: string;
    alt?: string;
  };
}

export default function InstitutionalImage({ value }: InstitutionalImageProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Disable scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  if (!value?.asset) return null;

  const imageUrl = urlForImage(value).url();

  return (
    <div className="my-12 group">
      {/* Inline Image Container */}
      <div
        className="relative cursor-zoom-in overflow-hidden border border-border/40 shadow-premium bg-muted/30"
        onClick={() => setIsOpen(true)}
      >
        <img
          src={imageUrl}
          alt={value.alt || 'World Prime Post Intelligence Graphic'}
          className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
          loading="lazy"
        />

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="bg-background/90 p-3 rounded-full shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
            <Maximize2 className="w-5 h-5 text-foreground" />
          </div>
        </div>
      </div>

      {/* Institutional Caption */}
      {value.caption && (
        <div className="mt-4 border-l-2 border-brand pl-4">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/50 font-medium mb-1">
            Institutional Registry Citation:
          </p>
          <p className="text-sm italic font-serif text-muted-foreground leading-relaxed">
            "{value.caption}"
          </p>
        </div>
      )}

      {/* Premium Fullscreen Lightbox */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 md:p-12"
            onClick={() => setIsOpen(false)}
          >
            {/* Close Button */}
            <button
              className="absolute top-8 right-8 z-[10000] p-4 bg-white/10 hover:bg-white/20 transition-colors rounded-full text-white group"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              aria-label="Close Lightbox"
            >
              <X className="w-8 h-8 group-hover:rotate-90 transition-transform duration-300" />
            </button>

            {/* Scale-up Image */}
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative max-w-full max-h-full flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imageUrl}
                alt={value.alt || 'Registry Item Full View'}
                className="max-w-full max-h-[85vh] object-contain shadow-2xl border border-white/10"
              />

              {value.caption && (
                <div className="mt-6 text-center max-w-2xl">
                  <p className="text-white font-serif italic text-lg opacity-90 leading-relaxed">
                    {value.caption}
                  </p>
                  <p className="mt-2 text-[10px] uppercase tracking-widest text-[#8B0000] font-bold">
                    Official Institutional Archive • Authenticated
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
